import SiteHeader from "@/components/SiteHeader";
import { PROGRAM_NAME } from "@/data/playbook";
import { STRATEGIST } from "@/data/strategist";

const UPDATED = "July 16, 2026";

const Section = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-xl font-bold text-slate-900 mb-3">
      <span className="text-brand font-mono mr-2">{String(n).padStart(2, "0")}</span>{title}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-page">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Legal</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900">Terms &amp; Conditions</h1>
        <p className="mt-4 text-slate-500">Last updated: {UPDATED}</p>

        <div className="mt-12">
          <Section n={1} title="Acceptance of Terms">
            <p>
              These Terms &amp; Conditions ("Terms") govern your access to and use of {PROGRAM_NAME} and the
              services operated by {STRATEGIST.business} (the "Service", "we", "us", "our"). By accessing or using
              the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
            </p>
          </Section>

          <Section n={2} title="Description of Service">
            <p>
              The Service provides educational resources for freight owner-operators — including the 12-Step
              Business Foundation Guide, a readiness diagnostic, a cost-per-mile calculator, a simulated load board, an
              AI coaching tool, and the ability to request a lane strategy consultation. Some features are free;
              others require a paid subscription.
            </p>
          </Section>

          <Section n={3} title="Not Professional, Legal, Financial or Tax Advice">
            <p>
              All content, scores, calculators, benchmarks and AI-generated guidance are provided for general
              informational and educational purposes only. They are estimates and do not constitute legal, tax,
              financial, insurance, regulatory or professional advice. Regulations (including FMCSA, IRS and state
              requirements) change and vary by situation. You are responsible for verifying current rules with the
              relevant authorities or a licensed professional before acting.
            </p>
          </Section>

          <Section n={4} title="Simulated Load Board &amp; Rates">
            <p>
              The load board and any rates, rate ranges, revenue-per-mile figures or "good/bad deal" indicators shown
              in the Service are <strong>simulated and illustrative</strong>, generated for demonstration, training and
              educational purposes only. They do <strong>not</strong> represent real, bookable freight or live offers
              from any broker or shipper.
            </p>
            <p>
              Simulated rates are estimates and <strong>may differ, sometimes significantly, from actual live market
              rates</strong>, which fluctuate constantly by lane, season, equipment, fuel prices and demand. Do not
              rely on the load board or its rates for actual load booking, pricing or business decisions. Always verify
              current rates against live market sources before acting.
            </p>
          </Section>

          <Section n={5} title="Accounts">
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all
              activity under your account. Provide accurate information and keep it up to date. We may suspend or
              terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section n={6} title="Subscriptions, Billing &amp; Cancellation">
            <p>
              Paid features are offered on a subscription basis (currently $49/month or $468/year). Payments are
              processed securely by our third-party payment processor (Stripe); we do not store your full card
              details. Subscriptions grant access for the applicable billing period. You may cancel at any time; access
              continues until the end of the current paid period. Except where required by law, fees already paid are
              non-refundable.
            </p>
          </Section>

          <Section n={7} title="Lead Submissions &amp; Consultations">
            <p>
              When you submit a strategy-session or contact request, you consent to us using the information you
              provide (such as name, email, phone and equipment details) to contact you about your inquiry. Requesting
              a consultation does not create a professional engagement until separately agreed in writing.
            </p>
          </Section>

          <Section n={8} title="Affiliate &amp; Third-Party Links">
            <p>
              The Service contains links to third-party resources and partners (for example, ELD, insurance,
              factoring and government sites). Some links may be affiliate links, meaning we may earn a commission at
              no additional cost to you. We do not control and are not responsible for third-party content, products
              or services. Inclusion of a link is not an endorsement.
            </p>
          </Section>

          <Section n={9} title="Intellectual Property">
            <p>
              The Service and all of its contents — including the 12-Step Business Foundation Guide, the readiness
              diagnostic and its scoring, the cost-per-mile calculator, the load-board format, benchmarks, the AI
              coaching prompts and outputs, all text, copy, graphics, logos, layouts, structure and underlying
              software — are the exclusive property of {STRATEGIST.business} (or its licensors) and are protected by
              copyright, trademark and other intellectual-property laws. All rights are reserved.
            </p>
            <p>
              We grant you a limited, personal, non-exclusive, non-transferable, revocable license to access and use
              the Service solely for your own individual business-planning purposes. This license grants you no
              ownership rights whatsoever.
            </p>
          </Section>

          <Section n={10} title="Restrictions — No Copying, Reselling or Recreating">
            <p>Except with our prior express written permission, you may <strong>not</strong>, in whole or in part:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>copy, reproduce, screenshot for redistribution, download, scrape, or store the content or Service for any purpose beyond your own personal use;</li>
              <li>republish, distribute, share, sublicense, sell, resell, rent, lease, or otherwise commercialize the content, the Guide, or any part of the Service;</li>
              <li>recreate, clone, rebuild, or produce a similar, competing, or derivative product, service, program, course, tool or website based on this Service or its content, structure or methodology;</li>
              <li>modify, translate, adapt, or create derivative works from the content;</li>
              <li>reverse-engineer, decompile, disassemble, or attempt to extract the source code, prompts, formulas, or data models behind the Service;</li>
              <li>remove, obscure, or alter any copyright, trademark, or proprietary notice; or</li>
              <li>use the content or Service to train, build, or improve any competing product, dataset, or artificial-intelligence model.</li>
            </ul>
            <p>
              <strong>Name &amp; trademark.</strong> "RouteHaul," "{STRATEGIST.business}," "{PROGRAM_NAME}," and our
              logos are our trademarks and service marks. You may <strong>not</strong> use the term "{PROGRAM_NAME}"
              or "RouteHaul" — in whole or in part, or any variation, abbreviation, misspelling, translation or
              confusingly similar term — on any website, application, product, service, domain name, social media
              handle, advertising or marketing, or in any other manner, without our prior written permission.
            </p>
            <p>
              These restrictions survive termination of your account or subscription. Unauthorized use is a breach of
              these Terms and may infringe our intellectual-property rights, entitling us to pursue all available legal
              and equitable remedies, including injunctive relief.
            </p>
          </Section>

          <Section n={11} title="Acceptable Use &amp; Prohibited Conduct">
            <p>
              You agree that there will be no activities, and you will not engage in any activity, in connection with
              the Service that violates or infringes any common-law or statutory rights of any person or other entity —
              including, without limitation, proprietary rights, copyright, trademark, service mark or patent rights, or
              any right of privacy or publicity. If you do, <strong>your right to use the Service will be terminated.</strong>
              {" "}Without limiting the foregoing, you agree that you will not:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>violate any applicable law, rule or regulation;</li>
              <li>post, use, or transmit any material or information that is obscene, pornographic, libelous, defamatory or slanderous;</li>
              <li>use any false or deceptive material or information, or otherwise engage in any consumer or other fraud;</li>
              <li>host, control, promote, operate or use a website or application that is identical to, copied in any way from, or that mirrors the look and feel of our Service, or that creates the impression that it is RouteHaul's application or is associated with, endorsed by, or mirrors {PROGRAM_NAME} in any manner; or</li>
              <li>attempt to gain unauthorized access to, overload, or interfere with the Service or its systems.</li>
            </ul>
            <p>
              Any violation may result in immediate termination of your access, without notice, and may subject you to
              civil and criminal liability.
            </p>
          </Section>

          <Section n={12} title="Privacy">
            <p>
              We collect and process the information you provide to operate the Service and to follow up on your
              requests. We do not sell your personal information. By using the Service you consent to this handling of
              your data. For any data request or deletion, contact us at the address below.
            </p>
          </Section>

          <Section n={13} title="Disclaimers &amp; Limitation of Liability">
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
              To the maximum extent permitted by law, {STRATEGIST.business} shall not be liable for any indirect,
              incidental, consequential or lost-profit damages arising from your use of, or inability to use, the
              Service or any reliance on its content. Business decisions you make are your own responsibility.
            </p>
          </Section>

          <Section n={14} title="Service Availability, Interruptions &amp; Changes">
            <p>
              The Service is provided on an "as available" basis and we do not guarantee that it will be uninterrupted,
              timely, secure, or error-free. To the maximum extent permitted by law, {STRATEGIST.business} shall
              <strong> not be liable</strong> for any loss, damage, or inconvenience arising from:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>internet, network, connectivity or power interruptions, outages, downtime, or slowdowns;</li>
              <li>maintenance, suspension, or discontinuation of the Service or any of its features;</li>
              <li>changes to our policies, pricing, plans, content, or these Terms;</li>
              <li>data loss, corruption, or unavailability; and</li>
              <li>the acts, omissions, failures, outages, security incidents, or policy changes of any third-party service or provider we rely on or link to — including, without limitation, our hosting, payment processor, email provider, and any external websites or partner resources (such as FMCSA, ELD, insurance or factoring providers).</li>
            </ul>
            <p>
              We may modify, suspend, or discontinue any part of the Service, and change our policies or pricing, at any
              time and without liability to you. Your sole remedy if you are dissatisfied is to stop using the Service.
            </p>
          </Section>

          <Section n={15} title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. Material changes will be reflected by updating the "Last
              updated" date above. Your continued use of the Service after changes take effect constitutes acceptance
              of the revised Terms.
            </p>
          </Section>

          <Section n={16} title="Governing Law &amp; Dispute Resolution">
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of California, USA,
              without regard to its conflict-of-laws principles. You agree that any dispute arising out of or relating
              to these Terms or the Service shall be subject to the exclusive jurisdiction of the state and federal
              courts located in California, and you consent to venue in those courts.
            </p>
          </Section>

          <Section n={17} title="Contact">
            <p>
              Questions about these Terms? Contact {STRATEGIST.name}, {STRATEGIST.title}, {STRATEGIST.business}:
              {" "}
              <a href={`mailto:${STRATEGIST.email}`} data-testid="terms-contact-email" className="text-brand font-bold hover:underline break-all">{STRATEGIST.email}</a>.
            </p>
          </Section>
        </div>

        <div className="border-t border-slate-200 pt-8 mt-4 text-sm text-slate-400">
          © {new Date().getFullYear()} {STRATEGIST.business}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
